import React, {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {getPublicFile} from 'helpers/envHelper'
import styled from 'styled-components'
import WrapElement from 'pages/HealthCenter/ReportV1/Elements/WrapElement'
import useHealthCenterBrainFunctionMappingStyles from 'pages/HealthCenter/ReportV1/BrainFunctionMapping/Style'
import TableFiguresByPart from 'pages/HealthCenter/ReportV1/BrainFunctionMapping/Elements/tableFiguresByPart'
import useLangEn from 'hooks/useLangEn'
import useLanguage from 'features/languageReport/useLanguage'

export const PageWrap = styled.div`
  background: White;
`

export const PageContent = styled.div`
  width: 1160px;
`

export const CardContent = styled.div`
  font-size: 14px;
`

export const FlexBox = styled.div`
  display: flex;
  padding: 0 15px;
  justify-content: space-between;
`
const getHealthReportReportImage = getPublicFile('images/health_center_report')

interface BrainFunctionMappingProps {
  agingLobeScore: Record<string, any>
  agingRoiScore: Record<string, any>
}

const getNumber = (value: number | string) => {
  if (!Number.isNaN(Number(value))) return Math.floor(Number(value))
  return 0
}

const scoreToString = (score: number) => {
  if (Math.floor(score) < 30) {
    return 'IScoreBrainMapping_Lowering'
  }
  if (Math.floor(score) > 70) {
    return 'IScoreBrainMapping_Exaltation'
  }

  return 'IScoreBrainMapping_Balance'
}

const scoreToClass = (score: number) => {
  if (Math.floor(score) < 30) {
    return 'LoweringActive'
  }
  if (Math.floor(score) > 70) {
    return 'ExaltationActive'
  }

  return 'Balanced'
}

const scoreSign = (score: number | string) => {
  const parsedScore =
    typeof score === 'string' ? Number.parseFloat(score) : score

  if (parsedScore < 30) {
    return getHealthReportReportImage('down.svg')
  }
  if (parsedScore > 70) {
    return getHealthReportReportImage('up.svg')
  }

  return getHealthReportReportImage('arrow.svg')
}

const Label = ({
  score,
  isLocalLanguage = true,
}: {
  score: number | string
  isLocalLanguage?: boolean
}) => {
  const {t} = useTranslation()
  const {textHealthAPI} = useLanguage('health')
  const classes = useHealthCenterBrainFunctionMappingStyles()
  const string = scoreToString(getNumber(score))
  const className = scoreToClass(getNumber(score))

  const scoreToStringAPI = useCallback((score: number) => {
    if (Math.floor(score) < 30) {
      return textHealthAPI?.brain_mapping.status.low
    }
    if (Math.floor(score) > 70) {
      return textHealthAPI?.brain_mapping.status.high
    }

    return textHealthAPI?.brain_mapping.status.normal
  }, [])
  return (
    <div className={`${classes.labelScore} ${className}`}>
      {isLocalLanguage ? t(string) : scoreToStringAPI(getNumber(score))}

      <img src={scoreSign(score)} alt='icon' />
    </div>
  )
}

const Score = ({score}: {score: number}) => {
  const {t, i18n} = useTranslation()
  const classes = useHealthCenterBrainFunctionMappingStyles()
  return (
    <div className={classes.bottom16}>
      <div className={classes.scoreNumber}>
        {score}
        {i18n.language !== 'en' && t('IScore')}
      </div>
      <Label score={score} isLocalLanguage={false} />
    </div>
  )
}

function BrainFunctionMappingPage({
  agingLobeScore,
  agingRoiScore,
}: BrainFunctionMappingProps) {
  const {t} = useTranslation()
  const {textHealthAPI} = useLanguage('health')
  const classes = useHealthCenterBrainFunctionMappingStyles()
  const isEn = useLangEn()

  const getHeadTable = useCallback(
    (number: number) => {
      let firstColumnText =
        textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
          .headers[0] || ''
      if (number === 2) {
        firstColumnText =
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .headers[0] || ''
      }
      if (number === 3) {
        firstColumnText =
          textHealthAPI?.brain_mapping.advanced.section.parietal.score_table
            .headers[0] || ''
      }
      if (number === 4) {
        firstColumnText =
          textHealthAPI?.brain_mapping.advanced.section.occipital.score_table
            .headers[0] || ''
      }
      return [
        firstColumnText,
        textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
          .headers[1] || '',
        textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
          .headers[2] || '',
        textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
          .headers[3] || '',
        textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
          .headers[4] || '',
      ]
    },
    [textHealthAPI],
  )

  const bodyTable = useMemo(
    () => [
      {
        data: [
          {
            header: [
              {
                key: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[0].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.frontal
                    .score_table.rows[0].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[0].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainF1.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[0].functions,
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[0].symptoms,
          [getNumber(agingRoiScore?.Frontal_Pole_LH)],
          [getNumber(agingRoiScore?.Frontal_Pole_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                key: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[1].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.frontal
                    .score_table.rows[1].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[1].rois[0].translation,
              },
              {
                key:
                  textHealthAPI?.brain_mapping.advanced.section.frontal
                    .score_table.rows[1].rois[1].code ?? '',
                content:
                  textHealthAPI?.brain_mapping.advanced.section.frontal
                    .score_table.rows[1].rois[1].name,
                note: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[1].rois[1].translation,
              },
              {
                key: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[1].rois[2].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.frontal
                    .score_table.rows[1].rois[2].name,
                note: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[1].rois[2].translation,
              },
            ],
            image: getHealthReportReportImage('brainF2.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[1].functions,
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[1].symptoms,
          [
            getNumber(agingRoiScore?.Pars_Opercularis_LH),
            getNumber(agingRoiScore?.Pars_Orbitalis_LH),
            getNumber(agingRoiScore?.Pars_Triangularis_LH),
          ],
          [
            getNumber(agingRoiScore?.Pars_Opercularis_RH),
            getNumber(agingRoiScore?.Pars_Orbitalis_RH),
            getNumber(agingRoiScore?.Pars_Triangularis_RH),
          ],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'F5.',
                // content: 'Rostral Middle Frontal',
                // note: t('IRostralNoteF5'),
                key: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[2].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.frontal
                    .score_table.rows[2].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[2].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainF5.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[2].functions,
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[2].symptoms,
          [getNumber(agingRoiScore?.Rostral_Middle_Frontal_LH)],
          [getNumber(agingRoiScore?.Rostral_Middle_Frontal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'F6.',
                // content: 'Caudal Middle Frontal',
                // note: t('ICaudalMiddle'),
                key: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[3].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.frontal
                    .score_table.rows[3].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[3].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainF6.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[3].functions,
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[3].symptoms,
          [getNumber(agingRoiScore?.Caudal_Middle_Frontal_LH)],
          [getNumber(agingRoiScore?.Caudal_Middle_Frontal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'F7.',
                // content: 'Superior Frontal',
                // note: t('IFrontalNoteF7'),
                key: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[4].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.frontal
                    .score_table.rows[4].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[4].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainF7.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[4].functions,
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[4].symptoms,
          [getNumber(agingRoiScore?.Superior_Frontal_LH)],
          [getNumber(agingRoiScore?.Superior_Frontal_RH)],
        ],
        background: 'White',
      },
    ],
    [isEn, textHealthAPI],
  )

  const bodyTable2 = useMemo(
    () => [
      {
        data: [
          {
            header: [
              {
                key: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[5].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.frontal
                    .score_table.rows[5].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[5].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainF8.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[5].functions,

          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[5].symptoms,
          [getNumber(agingRoiScore?.Medial_Orbitofrontal_LH)],
          [getNumber(agingRoiScore?.Medial_Orbitofrontal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'F9.',
                // content: 'Lateral Orbitofrontal',
                // note: t('ILateralF9'),
                key: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[6].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.frontal
                    .score_table.rows[6].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[6].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainF9.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[6].functions,
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[6].symptoms,
          [getNumber(agingRoiScore?.Lateral_Orbitofrontal_LH)],
          [getNumber(agingRoiScore?.Lateral_Orbitofrontal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'F10.',
                // content: 'Precentral',
                // note: t('IAnteriorF10'),
                key: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[7].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.frontal
                    .score_table.rows[7].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[7].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainF10.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[7].functions,
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[7].symptoms,
          [getNumber(agingRoiScore?.Precentral_LH)],
          [getNumber(agingRoiScore?.Precentral_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'F11.',
                // content: 'Paracentral',
                // note: t('IAtrialF11'),
                key: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[8].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.frontal
                    .score_table.rows[8].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[8].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainF11.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[8].functions,
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[8].symptoms,
          [getNumber(agingRoiScore?.Paracentral_LH)],
          [getNumber(agingRoiScore?.Paracentral_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'F12.',
                // content: 'Rostral Anterior Cingulate',
                // note: t('IAnteriorF12'),
                key: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[9].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.frontal
                    .score_table.rows[9].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[9].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainF12.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[9].functions,
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[9].symptoms,
          [getNumber(agingRoiScore?.Rostral_Anterior_Cingulate_LH)],
          [getNumber(agingRoiScore?.Rostral_Anterior_Cingulate_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'F13.',
                // content: 'Caudal Anterior Cingulate',
                // note: t('IAnteriorF13'),
                key: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[10].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.frontal
                    .score_table.rows[10].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.frontal
                  .score_table.rows[10].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainF13.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[10].functions,
          textHealthAPI?.brain_mapping.advanced.section.frontal.score_table
            .rows[10].symptoms,
          [getNumber(agingRoiScore?.Caudal_Anterior_Cingulate_LH)],
          [getNumber(agingRoiScore?.Caudal_Anterior_Cingulate_RH)],
        ],
        background: 'White',
      },
    ],
    [isEn, textHealthAPI],
  )

  const bodyTable3 = useMemo(
    () => [
      {
        data: [
          {
            header: [
              {
                // key: 'T1.',
                // content: 'Temporal Pole',
                // note: t('ITemporalPoleT1'),
                key: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[0].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.temporal
                    .score_table.rows[0].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[0].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainT1.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[0].functions,
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[0].symptoms,
          [getNumber(agingRoiScore?.Temporal_Pole_LH)],
          [getNumber(agingRoiScore?.Temporal_Pole_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'T2.',
                // content: 'Superior Temporal',
                // note: t('ISuperiorT2'),
                key: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[1].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.temporal
                    .score_table.rows[1].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[1].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainT2.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[1].functions,
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[1].symptoms,
          [getNumber(agingRoiScore?.Superior_Temporal_LH)],
          [getNumber(agingRoiScore?.Superior_Temporal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'T3.',
                // content: 'Middle Temporal',
                // note: t('IMiddleT3'),
                key: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[2].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.temporal
                    .score_table.rows[2].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[2].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainT3.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[2].functions,
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[2].symptoms,
          [getNumber(agingRoiScore?.Middle_Temporal_LH)],
          [getNumber(agingRoiScore?.Middle_Temporal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'T4.',
                // content: 'Inferior Temporal',
                // note: t('IInferiorT4'),
                key: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[3].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.temporal
                    .score_table.rows[3].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[3].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainT4.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[3].functions,
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[3].symptoms,
          [getNumber(agingRoiScore?.Inferior_Temporal_LH)],
          [getNumber(agingRoiScore?.Inferior_Temporal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'T5.',
                // content: 'Transverse Temporal',
                // note: t('ILateralT5'),
                key: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[4].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.temporal
                    .score_table.rows[4].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[4].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainT5.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[4].functions,
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[4].symptoms,
          [getNumber(agingRoiScore?.Transverse_Temporal_LH)],
          [getNumber(agingRoiScore?.Transverse_Temporal_RH)],
        ],
        background: 'White',
      },
    ],
    [isEn, textHealthAPI],
  )

  const bodyTable4 = useMemo(
    () => [
      {
        data: [
          {
            header: [
              {
                // key: 'T6.',
                // content: 'Banks of Superior Temporal Sulcus',
                // note: t('ISuperiorT6'),
                key: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[5].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.temporal
                    .score_table.rows[5].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[5].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainT6.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[5].functions,
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[5].symptoms,

          [getNumber(agingRoiScore?.Banks_of_Superior_Temporal_Sulcus_LH)],
          [getNumber(agingRoiScore?.Banks_of_Superior_Temporal_Sulcus_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'T7.',
                // content: 'Fusiform',
                // note: t('INoteT7'),
                key: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[6].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.temporal
                    .score_table.rows[6].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[6].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainT7.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[6].functions,
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[6].symptoms,

          [getNumber(agingRoiScore?.Fusiform_LH)],
          [getNumber(agingRoiScore?.Fusiform_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'T8.',
                // content: 'Entorhinal',
                // note: t('ICortexT8'),
                key: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[7].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.temporal
                    .score_table.rows[7].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[7].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainT8.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[7].functions,
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[7].symptoms,
          [getNumber(agingRoiScore?.Entorhinal_LH)],
          [getNumber(agingRoiScore?.Entorhinal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'T9.',
                // content: 'Parahippocampal',
                // note: t('IHippocampusT9'),
                key: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[8].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.temporal
                    .score_table.rows[8].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[8].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainT9.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[8].functions || [],
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[8].symptoms || [],

          [getNumber(agingRoiScore?.Parahippocampal_LH)],
          [getNumber(agingRoiScore?.Parahippocampal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'T10.',
                // content: 'Insula',
                // note: t('IInsulaT10'),
                key: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[9].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.temporal
                    .score_table.rows[9].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.temporal
                  .score_table.rows[9].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainT10.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[9].functions,
          textHealthAPI?.brain_mapping.advanced.section.temporal.score_table
            .rows[9].symptoms,
          [getNumber(agingRoiScore?.Insula_LH)],
          [getNumber(agingRoiScore?.Insula_RH)],
        ],
        background: 'White',
      },
    ],
    [isEn, textHealthAPI],
  )

  const bodyTable5 = useMemo(
    () => [
      {
        data: [
          {
            header: [
              {
                // key: 'P1.',
                // content: 'Postcentral',
                // note: t('IPostCentralLobeP1'),
                key: textHealthAPI?.brain_mapping.advanced.section.parietal
                  .score_table.rows[0].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.parietal
                    .score_table.rows[0].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.parietal
                  .score_table.rows[0].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainP1.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.parietal.score_table
            .rows[0].functions,
          textHealthAPI?.brain_mapping.advanced.section.parietal.score_table
            .rows[0].symptoms,
          [getNumber(agingRoiScore?.Postcentral_LH)],
          [getNumber(agingRoiScore?.Postcentral_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'P2.',
                // content: 'Superior Parietal',
                // note: t('ISuperiorParietalLobeP2'),
                key: textHealthAPI?.brain_mapping.advanced.section.parietal
                  .score_table.rows[1].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.parietal
                    .score_table.rows[1].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.parietal
                  .score_table.rows[1].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainP2.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.parietal.score_table
            .rows[1].functions,
          textHealthAPI?.brain_mapping.advanced.section.parietal.score_table
            .rows[1].symptoms,
          [getNumber(agingRoiScore?.Superior_Parietal_LH)],
          [getNumber(agingRoiScore?.Superior_Parietal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'P3.',
                // content: 'Inferior Parietal',
                // note: t('IInferiorParietalLobeP3'),
                key: textHealthAPI?.brain_mapping.advanced.section.parietal
                  .score_table.rows[2].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.parietal
                    .score_table.rows[2].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.parietal
                  .score_table.rows[2].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainP3.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.parietal.score_table
            .rows[2].functions,
          textHealthAPI?.brain_mapping.advanced.section.parietal.score_table
            .rows[2].symptoms,
          [getNumber(agingRoiScore?.Inferior_Parietal_LH)],
          [getNumber(agingRoiScore?.Inferior_Parietal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'P4.',
                // content: 'Supramarginal',
                // note: t('IAssociationP4'),
                key: textHealthAPI?.brain_mapping.advanced.section.parietal
                  .score_table.rows[3].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.parietal
                    .score_table.rows[3].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.parietal
                  .score_table.rows[3].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainP4.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.parietal.score_table
            .rows[3].functions,
          textHealthAPI?.brain_mapping.advanced.section.parietal.score_table
            .rows[3].symptoms,
          [getNumber(agingRoiScore?.Supramarginal_LH)],
          [getNumber(agingRoiScore?.Supramarginal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'P5.',
                // content: 'Precuneus',
                // note: t('IPreLeafletP5'),
                key: textHealthAPI?.brain_mapping.advanced.section.parietal
                  .score_table.rows[4].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.parietal
                    .score_table.rows[4].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.parietal
                  .score_table.rows[4].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainP5.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.parietal.score_table
            .rows[4].functions,
          textHealthAPI?.brain_mapping.advanced.section.parietal.score_table
            .rows[4].symptoms,
          [getNumber(agingRoiScore?.Precuneus_LH)],
          [getNumber(agingRoiScore?.Precuneus_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'P6.',
                // content: 'Posterior Cingulate',
                // note: t('IPosteriorCinCortexP6'),
                key: textHealthAPI?.brain_mapping.advanced.section.parietal
                  .score_table.rows[5].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.parietal
                    .score_table.rows[5].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.parietal
                  .score_table.rows[5].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainP6.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.parietal.score_table
            .rows[5].functions,
          textHealthAPI?.brain_mapping.advanced.section.parietal.score_table
            .rows[5].symptoms,
          [getNumber(agingRoiScore?.Posterior_Cingulate_LH)],
          [getNumber(agingRoiScore?.Posterior_Cingulate_RH)],
        ],
        background: 'White',
      },
    ],
    [isEn, textHealthAPI],
  )

  const bodyTable6 = useMemo(
    () => [
      {
        data: [
          {
            header: [
              {
                // key: 'O1.',
                // content: 'Lateral Occipital',
                // note: t('ILateralOccipitalLobeO1'),
                key: textHealthAPI?.brain_mapping.advanced.section.occipital
                  .score_table.rows[0].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.occipital
                    .score_table.rows[0].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.occipital
                  .score_table.rows[0].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainO1.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.occipital.score_table
            .rows[0].functions,
          textHealthAPI?.brain_mapping.advanced.section.occipital.score_table
            .rows[0].symptoms,
          [getNumber(agingRoiScore?.Lateral_Occipital_LH)],
          [getNumber(agingRoiScore?.Lateral_Occipital_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'O2.',
                // content: 'Cuneus',
                // note: t('ISnowLeafO2'),
                key: textHealthAPI?.brain_mapping.advanced.section.occipital
                  .score_table.rows[1].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.occipital
                    .score_table.rows[1].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.occipital
                  .score_table.rows[1].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainO2.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.occipital.score_table
            .rows[1].functions,
          textHealthAPI?.brain_mapping.advanced.section.occipital.score_table
            .rows[1].symptoms,
          [getNumber(agingRoiScore?.Cuneus_LH)],
          [getNumber(agingRoiScore?.Cuneus_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'O3.',
                // content: 'Pericalcarine',
                // note: t('IClawLobeO3'),
                key: textHealthAPI?.brain_mapping.advanced.section.occipital
                  .score_table.rows[2].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.occipital
                    .score_table.rows[2].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.occipital
                  .score_table.rows[2].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainO3.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.occipital.score_table
            .rows[2].functions,
          textHealthAPI?.brain_mapping.advanced.section.occipital.score_table
            .rows[2].symptoms,
          [getNumber(agingRoiScore?.Pericalcarine_LH)],
          [getNumber(agingRoiScore?.Pericalcarine_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'O4.',
                // content: 'Lingual',
                // note: t('ISnowO4'),
                key: textHealthAPI?.brain_mapping.advanced.section.occipital
                  .score_table.rows[3].rois[0].code,
                content:
                  textHealthAPI?.brain_mapping.advanced.section.occipital
                    .score_table.rows[3].rois[0].name,
                note: textHealthAPI?.brain_mapping.advanced.section.occipital
                  .score_table.rows[3].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainO4.png'),
          },
          textHealthAPI?.brain_mapping.advanced.section.occipital.score_table
            .rows[3].functions,
          textHealthAPI?.brain_mapping.advanced.section.occipital.score_table
            .rows[3].symptoms,
          [getNumber(agingRoiScore?.Lingual_LH)],
          [getNumber(agingRoiScore?.Lingual_RH)],
        ],
        background: 'White',
      },
    ],
    [isEn, textHealthAPI],
  )
  return (
    <div className={classes.brainFunctionMapping}>
      <PageWrap>
        <PageContent>
          <WrapElement
            number={1}
            text={
              textHealthAPI?.brain_mapping.advanced.section.frontal.title || ''
            }
          >
            <CardContent>
              <FlexBox>
                <div className={classes.contentDetailedAnalysis}>
                  <div className={classes.textHeader}>
                    <span>
                      {
                        textHealthAPI?.brain_mapping.advanced.section.frontal
                          .description[0]
                      }
                    </span>
                    &nbsp;
                    <span className={classes.headerColorGreen}>
                      {/* {t('IResOfHightOrder')} */}
                      {
                        textHealthAPI?.brain_mapping.advanced.section.frontal
                          .description[1]
                      }
                    </span>
                    &nbsp;
                    <span>{t('IMainArea')}</span>
                  </div>
                  <div className={classes.textContent}>
                    {/* <p>{t('IGovernsHigher')}</p>
                    <p>{t('IRepresentativeExample')}</p> */}
                    <p>
                      {
                        textHealthAPI?.brain_mapping.advanced.section.frontal
                          .description[2]
                      }
                    </p>
                    <p>
                      {
                        textHealthAPI?.brain_mapping.advanced.section.frontal
                          .description[3]
                      }
                    </p>
                    <p>
                      {
                        textHealthAPI?.brain_mapping.advanced.section.frontal
                          .description[4]
                      }
                    </p>
                    {/* <p>{t('IDetailedAnalysis')}</p>
                    <p>{t('IHealthLevelOfEach')}</p>
                    <p>{t('ISuggestedLowerNumber')}</p> */}
                  </div>
                </div>
                <div className={classes.imgDetailedAnalysis}>
                  <div className={classes.scoreSpace}>
                    <div className={classes.scoreContainer}>
                      <div>
                        <div className={classes.headerScore}>
                          {
                            textHealthAPI?.brain_mapping.advanced.section
                              .frontal.score_by_side.left
                          }
                        </div>
                        <Score score={getNumber(agingLobeScore?.Frontal_LH)} />
                      </div>
                    </div>
                    <div className={classes.scoreContainer}>
                      <div>
                        <div className={classes.headerScore}>
                          {
                            textHealthAPI?.brain_mapping.advanced.section
                              .frontal.score_by_side.right
                          }
                        </div>
                        <Score score={getNumber(agingLobeScore?.Frontal_RH)} />
                      </div>
                    </div>
                  </div>
                  <div className={classes.imageOverviewBrain}>
                    <img src={getHealthReportReportImage('brain.svg')} alt='' />
                  </div>
                </div>
              </FlexBox>
            </CardContent>
          </WrapElement>
          <div className={classes.marginBottom60}>
            <TableFiguresByPart
              head={getHeadTable(1)}
              body={bodyTable}
              isLocalLanguage={false}
            />
          </div>

          <div className={classes.marginBottom60}>
            <TableFiguresByPart
              head={getHeadTable(1)}
              body={bodyTable2}
              isLocalLanguage={false}
            />
          </div>

          <WrapElement
            number={2}
            text={
              textHealthAPI?.brain_mapping.advanced.section.temporal.title || ''
            }
          >
            <CardContent>
              <FlexBox>
                <div className={classes.contentDetailedAnalysis}>
                  <div className={classes.textHeader}>
                    <span>
                      {
                        textHealthAPI?.brain_mapping.advanced.section.temporal
                          .description[0]
                      }
                    </span>
                    &nbsp;
                    <span className={classes.headerColorGreen}>
                      {
                        textHealthAPI?.brain_mapping.advanced.section.temporal
                          .description[1]
                      }
                    </span>
                    &nbsp;
                    <span>
                      {
                        textHealthAPI?.brain_mapping.advanced.section.temporal
                          .description[2]
                      }
                    </span>
                  </div>
                  <div className={classes.textContent}>
                    <p>
                      {
                        textHealthAPI?.brain_mapping.advanced.section.temporal
                          .description[3]
                      }
                    </p>
                    {/* <p>{t('ITemporalLobeDisorders')}</p>
                    <p>{t('IDetailedAnalysis2')}</p> */}
                  </div>
                </div>
                <div className={classes.imgDetailedAnalysis}>
                  <div className={classes.scoreSpace}>
                    <div className={classes.scoreContainer}>
                      <div>
                        <div className={classes.headerScore}>
                          {
                            textHealthAPI?.brain_mapping.advanced.section
                              .temporal.score_by_side.left
                          }
                        </div>
                        <Score score={getNumber(agingLobeScore?.Temporal_LH)} />
                      </div>
                    </div>
                    <div className={classes.scoreContainer}>
                      <div>
                        <div className={classes.headerScore}>
                          {
                            textHealthAPI?.brain_mapping.advanced.section
                              .temporal.score_by_side.right
                          }
                        </div>
                        <Score score={getNumber(agingLobeScore?.Temporal_RH)} />
                      </div>
                    </div>
                  </div>
                  <div className={classes.imageOverviewBrain}>
                    <img
                      src={getHealthReportReportImage('brainPage2.png')}
                      alt=''
                    />
                  </div>
                </div>
              </FlexBox>
            </CardContent>
          </WrapElement>

          <div className={classes.marginBottom60}>
            <TableFiguresByPart
              head={getHeadTable(2)}
              body={bodyTable3}
              isLocalLanguage={false}
            />
            <div className={classes.textBottomTable}>
              {
                textHealthAPI?.brain_mapping.basic.section.lobes.score_table
                  .annotation
              }
            </div>
          </div>

          <div className={classes.marginBottom60}>
            <TableFiguresByPart
              head={getHeadTable(2)}
              body={bodyTable4}
              isLocalLanguage={false}
            />
          </div>

          <WrapElement
            number={3}
            text={
              textHealthAPI?.brain_mapping.advanced.section.parietal.title || ''
            }
          >
            <CardContent>
              <FlexBox>
                <div className={classes.contentDetailedAnalysis}>
                  <div className={classes.textHeader}>
                    <span>
                      {
                        textHealthAPI?.brain_mapping.advanced.section.parietal
                          .description[0]
                      }
                    </span>
                    &nbsp;
                    <span className={classes.headerColorGreen}>
                      {/* {t('IResponsible')} */}
                      {
                        textHealthAPI?.brain_mapping.advanced.section.parietal
                          .description[1]
                      }
                    </span>
                    &nbsp;
                    <span>
                      {
                        textHealthAPI?.brain_mapping.advanced.section.parietal
                          .description[2]
                      }
                    </span>
                  </div>
                  <div className={classes.textContent}>
                    <p>
                      {
                        textHealthAPI?.brain_mapping.advanced.section.parietal
                          .description[3]
                      }
                    </p>
                    {/* <p>{t('ISymptomsLikeThese')}</p>
                    <p>{t('IHealthLevelOfEachDetailed')}</p>
                    <p>{t('ILowerNumber')}</p> */}
                  </div>
                </div>
                <div className={classes.imgDetailedAnalysis}>
                  <div className={classes.scoreSpace}>
                    <div className={classes.scoreContainer}>
                      <div>
                        <div className={classes.headerScore}>{t('ILeft')}</div>
                        <Score score={getNumber(agingLobeScore?.Parietal_LH)} />
                      </div>
                    </div>
                    <div className={classes.scoreContainer}>
                      <div>
                        <div className={classes.headerScore}>{t('IRight')}</div>
                        <Score score={getNumber(agingLobeScore?.Parietal_RH)} />
                      </div>
                    </div>
                  </div>
                  <div className={classes.imageOverviewBrain}>
                    <img
                      src={getHealthReportReportImage('brainPage5.png')}
                      alt=''
                    />
                  </div>
                </div>
              </FlexBox>
            </CardContent>
          </WrapElement>

          <div className={classes.marginBottom60}>
            <TableFiguresByPart
              head={getHeadTable(3)}
              body={bodyTable5}
              isLocalLanguage={false}
            />
          </div>

          <WrapElement
            number={4}
            text={
              textHealthAPI?.brain_mapping.advanced.section.occipital.title ||
              ''
            }
          >
            <CardContent>
              <FlexBox>
                <div className={classes.contentDetailedAnalysis}>
                  <div className={classes.textHeader}>
                    <span>
                      {
                        textHealthAPI?.brain_mapping.advanced.section.occipital
                          .description[0]
                      }
                    </span>
                    &nbsp;
                    <span className={classes.headerColorGreen}>
                      {/* {t('IChargeOfVision')} */}
                      {
                        textHealthAPI?.brain_mapping.advanced.section.occipital
                          .description[1]
                      }
                    </span>
                    &nbsp;
                    <span>
                      {
                        textHealthAPI?.brain_mapping.advanced.section.occipital
                          .description[2]
                      }
                    </span>
                  </div>
                  <div className={classes.textContent}>
                    <p>
                      {
                        textHealthAPI?.brain_mapping.advanced.section.occipital
                          .description[3]
                      }
                    </p>
                    {/* <p>{t('IHealthLevelOfEachDetailedArea')}</p>
                    <p>{t('IAssociatedSeizures')}</p>
                    <p>{t('IStandardizedRelative')}</p>
                    <p>{t('ILowerNumberV4')}</p> */}
                  </div>
                </div>
                <div className={classes.imgDetailedAnalysis}>
                  <div className={classes.scoreSpace}>
                    <div className={classes.scoreContainer}>
                      <div>
                        <div className={classes.headerScore}>
                          {
                            textHealthAPI?.brain_mapping.advanced.section
                              .occipital.score_by_side.left
                          }
                        </div>
                        <Score
                          score={getNumber(agingLobeScore?.Occipital_LH)}
                        />
                      </div>
                    </div>
                    <div className={classes.scoreContainer}>
                      <div>
                        <div className={classes.headerScore}>
                          {
                            textHealthAPI?.brain_mapping.advanced.section
                              .occipital.score_by_side.right
                          }
                        </div>
                        <Score
                          score={getNumber(agingLobeScore?.Occipital_RH)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={classes.imageOverviewBrain}>
                    <img
                      src={getHealthReportReportImage('brainPage6.png')}
                      alt=''
                    />
                  </div>
                </div>
              </FlexBox>
            </CardContent>
          </WrapElement>

          <div className={classes.marginBottom60}>
            <TableFiguresByPart
              head={getHeadTable(4)}
              body={bodyTable6}
              isLocalLanguage={false}
            />
          </div>
          <div className={classes.footer}>
            {textHealthAPI?.common?.footer?.[0] ?? ''}
          </div>
        </PageContent>
      </PageWrap>
    </div>
  )
}

export default BrainFunctionMappingPage
